import { createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import { type FC, lazy, type LazyExoticComponent } from "react";
import { RootLayout } from "./routes/__root";
import { IndexPage } from "./routes/index";

const rootRoute = createRootRoute({ component: RootLayout });

/**
 * Route code-splitting (PERF-P1).
 *
 * Every route except `/` is loaded on demand: its page component (and the whole
 * module subgraph it imports — heavy poster/QR deps, page-specific helpers, etc.)
 * lands in its own Rollup chunk instead of the boot bundle. `React.lazy` fetches
 * the chunk the first time the route is entered; the `<Suspense>` boundary in
 * `RootLayout` shows a fallback while it streams in (no white screen).
 *
 * `/` (IndexPage) stays eager — it's the Mini-App's boot landing, so lazying it
 * would just add a round-trip before the first paint with no first-load saving.
 *
 * `import().then((m) => ({ default: m.XPage }))` adapts each route module's named
 * page export to the default-export shape `React.lazy` expects, so the route
 * files keep their existing `export function XPage()` signatures untouched.
 */
function lazyPage(
  loader: () => Promise<Record<string, unknown>>,
  key: string,
): LazyExoticComponent<FC> {
  return lazy(() => loader().then((m) => ({ default: m[key] as FC })));
}

// Thin helper so route declarations stay flat. `path` is cast because each
// concrete literal would need its own generic propagation; we accept that
// trade-off for readability. `component` is a function component (eager
// IndexPage or a `React.lazy` wrapper) — both render fine as a route component.
function leaf(path: string, component: FC | LazyExoticComponent<FC>) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path: path as never,
    component,
  });
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexPage,
});

const routes = [
  indexRoute,
  leaf("/app", lazyPage(() => import("./routes/app"), "AppPreviewPage")),
  // Browse / shopping
  leaf("/catalog", lazyPage(() => import("./routes/catalog"), "CatalogPage")),
  leaf("/categories", lazyPage(() => import("./routes/categories"), "CategoriesPage")),
  leaf("/product/$channel/$spuNo", lazyPage(() => import("./routes/product"), "ProductPage")),
  leaf("/shop/$shopCode", lazyPage(() => import("./routes/shop"), "ShopPage")),
  leaf("/cart", lazyPage(() => import("./routes/cart"), "CartPage")),
  leaf("/confirm-pay", lazyPage(() => import("./routes/confirm-pay"), "ConfirmPayPage")),
  // Pay outcomes
  leaf("/pay-success", lazyPage(() => import("./routes/pay-success"), "PaySuccessPage")),
  leaf("/pay-cancel", lazyPage(() => import("./routes/pay-cancel"), "PayCancelPage")),
  leaf("/pay-result", lazyPage(() => import("./routes/pay-result"), "PayResultPage")),
  // Orders
  leaf("/orders", lazyPage(() => import("./routes/orders"), "OrdersPage")),
  leaf("/order-detail/$orderNo", lazyPage(() => import("./routes/order-detail"), "OrderDetailPage")),
  leaf(
    "/supplementary-payment",
    lazyPage(() => import("./routes/supplementary-payment"), "SupplementaryPaymentPage"),
  ),
  // Logistics + warehouse
  leaf("/warehouse", lazyPage(() => import("./routes/warehouse"), "WarehousePage")),
  leaf("/submit-parcel", lazyPage(() => import("./routes/submit-parcel"), "SubmitParcelPage")),
  leaf("/shipment", lazyPage(() => import("./routes/shipment"), "ShipmentPage")),
  leaf("/waybills", lazyPage(() => import("./routes/waybills"), "WaybillsPage")),
  leaf("/transport", lazyPage(() => import("./routes/transport"), "TransportPage")),
  leaf(
    "/transport-records",
    lazyPage(() => import("./routes/transport-records"), "TransportRecordsPage"),
  ),
  leaf("/transport-roam", lazyPage(() => import("./routes/transport-roam"), "TransportRoamPage")),
  leaf("/estimation", lazyPage(() => import("./routes/estimation"), "EstimationPage")),
  // Account
  leaf("/profile", lazyPage(() => import("./routes/profile"), "ProfilePage")),
  leaf("/favorite", lazyPage(() => import("./routes/favorite"), "FavoritePage")),
  leaf("/address", lazyPage(() => import("./routes/address"), "AddressPage")),
  leaf("/wallet", lazyPage(() => import("./routes/wallet"), "WalletPage")),
  leaf("/withdraw", lazyPage(() => import("./routes/withdraw"), "WithdrawPage")),
  leaf("/bill", lazyPage(() => import("./routes/bill"), "BillPage")),
  leaf("/arrears", lazyPage(() => import("./routes/arrears"), "ArrearsPage")),
  leaf(
    "/remittance-record",
    lazyPage(() => import("./routes/remittance-record"), "RemittanceRecordPage"),
  ),
  leaf("/message", lazyPage(() => import("./routes/message"), "MessagePage")),
  leaf("/notice", lazyPage(() => import("./routes/notice"), "NoticePage")),
  // Auth
  leaf("/login", lazyPage(() => import("./routes/login"), "LoginPage")),
  leaf("/register", lazyPage(() => import("./routes/register"), "RegisterPage")),
  leaf("/forgot", lazyPage(() => import("./routes/forgot"), "ForgotPage")),
  // Marketing
  // /affiliate is the single推广联盟 page (full version with rate ladder,
  // calculator, leaderboard, invite tools, poster). The duplicate /invite page
  // and its orphaned /invite-{commission,personal,team,record} stubs were removed
  // (#929 ⑤) — every entry point now goes to /affiliate.
  leaf("/affiliate", lazyPage(() => import("./routes/affiliate"), "AffiliatePage")),
  leaf("/points", lazyPage(() => import("./routes/points"), "PointsPage")),
  leaf("/rehearsal", lazyPage(() => import("./routes/rehearsal"), "RehearsalPage")),
  leaf("/shopwindow", lazyPage(() => import("./routes/shopwindow"), "ShopwindowPage")),
  // Info
  leaf("/legal", lazyPage(() => import("./routes/legal"), "LegalPage")),
  leaf("/tariffs", lazyPage(() => import("./routes/tariffs"), "TariffsPage")),
  leaf("/prohibited", lazyPage(() => import("./routes/prohibited"), "ProhibitedPage")),
  leaf("/contacts", lazyPage(() => import("./routes/contacts"), "ContactsPage")),
  leaf("/business", lazyPage(() => import("./routes/business"), "BusinessPage")),
  leaf("/review-consult", lazyPage(() => import("./routes/review-consult"), "ReviewConsultPage")),
  leaf("/help", lazyPage(() => import("./routes/help"), "HelpPage")),
  leaf("/beginner-guide", lazyPage(() => import("./routes/beginner-guide"), "BeginnerGuidePage")),
];

// TanStack Router infers a readonly tuple for `addChildren` to give typed
// routes; we keep route list in a regular array (~30 entries) and cast —
// typed `Link to` is achieved via per-call `as never` casts.
// biome-ignore lint/suspicious/noExplicitAny: see above
const routeTree = rootRoute.addChildren(routes as any);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
